// Cold-start retry primitives for the surface runtime (layered shell surfaces, WS-2 Q-3).
//
// A scale-from-zero ("cold") fixture's FIRST request returns `502` (often with
// `x-keda-http-cold-start: true`) from the KEDA-HTTP interceptor while the pod
// is still warming. Surfacing that as a load error forces the user to refresh.
// These helpers re-attempt the load once the pod is warm, automatically and
// bounded.
//
// Copied from `@zion/sdk-react`'s `transport/cold-start.ts` and
// `transport/cold-start-fetch.ts` under the same names, rules and signatures.
// `@zion/sdk-react` keeps its copy unchanged: this package has zero runtime
// dependencies (D12), so it cannot import one.
// The first `import()` 502s, then in the few ms before the probe runs the pod
// often warms, so the probe GET returns 200. A 200 probe is therefore the
// STRONGEST signal that retrying NOW will succeed — it must NOT be read as
// "not a cold start, give up". The probe maps to one of three decisions:
//   • 'warm'      — 2xx → pod Ready → retry immediately (no backoff).
//   • 'cold'      — 502/503/504, the cold-start header, OR a SAME-ORIGIN
//                   network error (a still-cold pod can refuse the
//                   connection) → wait the backoff and retry. Bounded by
//                   maxRetries either way.
//   • 'permanent' — any non-2xx that is not a cold-start signal (e.g. 403,
//                   404, 500, …), OR a CROSS-ORIGIN network error → bail, do
//                   not retry; a genuine missing/forbidden/broken bundle
//                   fails fast.
/** True when a response carries a KEDA-HTTP cold-start signal (the explicit
 * interceptor marker, or a 502/503/504 from the cold upstream hop). Retained
 * as the building block `classifyProbeResponse` composes. */
export function isColdStartResponse(res) {
    if (res.headers.get('x-keda-http-cold-start') === 'true')
        return true;
    return res.status === 502 || res.status === 503 || res.status === 504;
}
/**
 * Classify a probe response into a retry decision.
 *   • cold-start signal (header / 502 / 503 / 504) → 'cold' (wait + retry)
 *   • any other 2xx → 'warm' (pod Ready → retry NOW)
 *   • anything else (definitive 403 / 404 / other) → 'permanent' (bail)
 */
export function classifyProbeResponse(res) {
    if (isColdStartResponse(res))
        return 'cold';
    if (res.status >= 200 && res.status < 300)
        return 'warm';
    return 'permanent';
}
/** The document's current URL, or `undefined` outside a browser (SSR, plain
 * node, a test runner with no DOM). Widened to `Location | undefined` on
 * purpose: `lib.DOM` types `globalThis.location` as always-present, which is
 * not true everywhere this SDK runs, and a non-browser host must not crash a
 * probe. */
function currentHref() {
    const loc = globalThis.location;
    return loc?.href;
}
/**
 * True when `url` resolves to the SAME ORIGIN as the current document.
 * Relative URLs (`/bundle.js`, `./x.js`) are same-origin by definition.
 *
 * Why origin, and not the error object: in a browser `fetch()` rejects with an
 * opaque `TypeError` for DNS failure, TLS/certificate error, CORS rejection AND
 * connection-refused ALIKE. Those are NOT distinguishable by inspecting the
 * error — no `err.code`, no useful `err.cause`. Origin is the one property that
 * IS decidable at the call site, and it is sufficient: KEDA scale-from-zero
 * semantics can only apply to a bundle served through THIS data plane's own
 * origin (its nginx → activator → KEDA-interceptor path). A cross-origin host
 * has no scale-from-zero behaviour by construction, so a throw against it can
 * never be a warming pod.
 *
 * ⛔ FAIL SAFE, NOT FAIL AGGRESSIVE. Both "no `location`" (SSR/node/test) and
 * "malformed URL" return `true`, which routes the caller to today's `'cold'`
 * behaviour. An unparseable input must never silently make the probe MORE
 * aggressive than it was before this function existed.
 *
 * ⚠️ Same-origin is the OBSERVED and SCHEMA-MANDATED shape for app bundles —
 * `fixture.schema.json` declares `bundleUrl` origin-less (`^/_apps/…`) and Ajv
 * enforces that at two gates (CP `bundle-parser.ts`, DP `manifest-validator.ts`)
 * — but it is **NOT enforced on the `POST /v1/versions` write path**
 * (`register-version.dto.ts` takes a bare `z.string()`, and that unvalidated
 * value OUTRANKS the validated one in `operator-api.service.ts`). So do not read
 * this function as proof that a bundle URL is guaranteed same-origin; it is not.
 * Closing that hole (host allowlist + CSP on every bundle loader) is **#1181**.
 */
export function isSameOriginUrl(url) {
    const here = currentHref();
    if (here === undefined)
        return true; // no document to compare against
    try {
        return new URL(url, here).origin === new URL(here).origin;
    }
    catch {
        return true; // unparseable — preserve the pre-existing 'cold' behaviour
    }
}
/**
 * Probe a bundle URL to decide whether (and how) to retry a failed load.
 *
 * A probe THROW is classified by origin (see {@link isSameOriginUrl}):
 *   • same-origin  → 'cold'      — a scaled-from-zero pod can refuse the TCP
 *                                  connection mid-warm, so the throw is
 *                                  genuinely ambiguous; retry after backoff.
 *   • cross-origin → 'permanent' — DNS / TLS / CORS against a host with no
 *                                  KEDA scale-from-zero behaviour. Retrying
 *                                  cost every user 600+1200+2400 = 4,200 ms of
 *                                  pure backoff on every load, forever (#798).
 *
 * Neither the probed URL nor any response body is logged — a bundle URL can
 * carry a signed-asset query, and this primitive runs on every failed load.
 */
export async function probeColdStart(url, fetchFn) {
    try {
        return classifyProbeResponse(await fetchFn(url, { method: 'GET' }));
    }
    catch {
        return isSameOriginUrl(url) ? 'cold' : 'permanent';
    }
}
/** Backoff (ms) before cold-start retry `attempt` (0-indexed): 600ms, 1.2s, 2.4s.
 * A scaled-from-zero Node fixture is typically Ready within a few seconds. */
export function coldStartBackoffMs(attempt) {
    return 600 * 2 ** attempt;
}
/**
 * Append a cache-buster query so a RETRIED dynamic import re-fetches the
 * bundle. `attempt` 0 returns the URL unchanged (the first, honest request);
 * attempt ≥ 1 appends `cs=<attempt>` (using `&` when the URL already has a
 * query). A browser caches a FAILED dynamic-import module-map entry and can
 * serve the cached rejection without re-fetching, so retrying the identical
 * specifier is a no-op — the distinct specifier forces a fresh fetch.
 *
 * Safe for the bundle's own relative imports/sourcemap: ESM relative
 * resolution uses the path up to the last `/`, which a trailing query on the
 * entry module does not change, and the bundler emits absolute sourcemap URLs.
 */
export function bustBundleUrl(url, attempt) {
    if (attempt <= 0)
        return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}cs=${attempt}`;
}
/**
 * Run `load(attempt)`; if it throws, `probe()` decides what to do:
 *   • 'permanent' → rethrow now (no retry).
 *   • 'warm'      → retry immediately (pod is Ready; no backoff).
 *   • 'cold'      → back off, then retry.
 * Bounded by `maxRetries` (default 3); the load receives the 0-indexed attempt
 * so the caller can cache-bust the retried specifier. Bounded automatic
 * equivalent of a manual browser refresh.
 */
export async function loadWithColdStartRetry(load, probe, sleep, options = {}) {
    const maxRetries = options.maxRetries ?? 3;
    const backoffMs = options.backoffMs ?? coldStartBackoffMs;
    for (let attempt = 0;; attempt++) {
        try {
            return await load(attempt);
        }
        catch (err) {
            // Short-circuit BEFORE probing — once retries are exhausted there is no
            // point firing a wasted network probe right before rethrowing.
            if (attempt >= maxRetries)
                throw err;
            const decision = await probe();
            if (decision === 'permanent')
                throw err;
            if (decision === 'cold')
                await sleep(backoffMs(attempt));
        }
    }
}
// ── Mutation guard (PR1 §5.B.3 / §5.E.2) ─────────────────────────────────────
// The retry-aware transport auto-retries a cold-start failure ONLY when the
// request is safe to replay: GET/HEAD are idempotent by definition; any other
// method is replayed only when it carries an `Idempotency-Key` header (so the
// server dedups on that key). A keyless mutation is the app's explicit opt-out
// of auto-retry — silently re-issuing a non-idempotent write is the worse
// failure, so its cold response surfaces to the caller unretried.
/** Resolve the effective HTTP method for a fetch invocation: `init.method`,
 * else a `Request` input's method, else `GET`. */
export function resolveRequestMethod(input, init) {
    if (init?.method)
        return init.method;
    if (input instanceof Request)
        return input.method;
    return 'GET';
}
/** True when the request carries an `Idempotency-Key` header — on the
 * `RequestInit` headers or on a `Request` input. */
export function requestCarriesIdempotencyKey(input, init) {
    if (new Headers(init?.headers).has('Idempotency-Key'))
        return true;
    if (input instanceof Request)
        return input.headers.has('Idempotency-Key');
    return false;
}
/** Mutation guard for cold-start auto-retry: GET/HEAD are always safe to
 * replay; any other method is replayed ONLY when it carries an
 * `Idempotency-Key`. */
export function isColdStartRetryable(input, init) {
    const method = resolveRequestMethod(input, init).toUpperCase();
    if (method === 'GET' || method === 'HEAD')
        return true;
    return requestCarriesIdempotencyKey(input, init);
}
/**
 * The ceiling, in seconds, of a wait that a `Retry-After` header sets: 120, the
 * `MAX_BACKOFF_DELAY_SEC` of `@zion/sdk-react`. A wait above 2147483647 ms makes `setTimeout` fire
 * after 1 ms (issue #3699). This package has zero runtime dependencies, so it keeps its own.
 */
const MAX_BACKOFF_DELAY_SEC = 120;
/** Cold-start `Retry-After` parse: a finite positive integer up to 120, else 1s. */
function coldStartRetryAfterSec(retryAfterHeader) {
    if (retryAfterHeader === null)
        return 1;
    const sec = parseInt(retryAfterHeader, 10);
    return Number.isFinite(sec) && sec > 0 ? Math.min(sec, MAX_BACKOFF_DELAY_SEC) : 1;
}
/**
 * The signal `fetch` obeys for one call: `init.signal` when it is not `undefined` (`null` means
 * none), else the signal of a `Request` input, else `null`. A copy of `callerSignalOf` in
 * `@zion/sdk-react`.
 */
function callerSignalOf(input, init) {
    if (init?.signal !== undefined)
        return init.signal;
    return input instanceof Request ? input.signal : null;
}
/**
 * Wait `ms` milliseconds on the default timer, or reject at once with the signal's reason when it
 * aborts. Every exit clears the timer and removes the abort listener. A copy of the wait part of
 * `startWait` in `@zion/sdk-react`, with no wake.
 */
function abortableDelay(ms, signal) {
    return new Promise((resolve, reject) => {
        if (signal === null) {
            setTimeout(resolve, ms);
            return;
        }
        if (signal.aborted) {
            reject(signal.reason);
            return;
        }
        const onAbort = () => {
            clearTimeout(timer);
            reject(signal.reason);
        };
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, { once: true });
    });
}
/**
 * Run an injected sleep, and reject at once with the signal's reason when it aborts. The injected
 * sleep runs on to its own end. An already aborted signal rejects and does not call `sleep`. A copy
 * of `sleepUnlessAborted` in `@zion/sdk-react`.
 */
function sleepUnlessAborted(sleep, ms, signal) {
    if (signal === null)
        return sleep(ms);
    if (signal.aborted)
        return Promise.reject(signal.reason);
    return new Promise((resolve, reject) => {
        const onAbort = () => {
            reject(signal.reason);
        };
        signal.addEventListener('abort', onAbort, { once: true });
        void sleep(ms)
            .then(resolve, reject)
            .finally(() => {
            signal.removeEventListener('abort', onAbort);
        });
    });
}
function classifyColdStart(res) {
    const retryAfter = res.headers.get('Retry-After');
    if (res.status === 431)
        return { retry: false };
    if (res.headers.get('x-keda-http-cold-start') === 'true' || res.status === 502 || res.status === 504) {
        return { retry: true, retryAfterSec: coldStartRetryAfterSec(retryAfter) };
    }
    if (res.status === 503 && retryAfter)
        return { retry: false };
    if (res.status === 503)
        return { retry: true, retryAfterSec: coldStartRetryAfterSec(retryAfter) };
    return { retry: false };
}
/** Promise-based sleep, the default backoff timer. Resolves after `ms` milliseconds. */
export function defaultSleep(ms) {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
}
let activeSleep = defaultSleep;
/** The backoff timer the runtime's loader and stylesheet fetch use: {@link defaultSleep} unless a test installed another. */
export function coldStartSleep() {
    return activeSleep;
}
/**
 * Install `next` as the runtime's backoff timer. Returns a function that restores the timer that
 * was active before this call. Test seam; production never calls it.
 */
export function setColdStartSleep(next) {
    const previous = activeSleep;
    activeSleep = next;
    return () => {
        activeSleep = previous;
    };
}
/** Put {@link defaultSleep} back. `resetSurfaceRuntime` in `./testing` is the only caller. */
export function resetColdStartSleep() {
    activeSleep = defaultSleep;
}
/**
 * Wrap a `fetch` with bounded cold-start retry. On a cold-start classification (502 / 504 /
 * plain 503 / `x-keda-http-cold-start`) the wrapper re-issues the request up to `maxRetries` times
 * (default 4), waiting `max(backoffMs(attempt), Retry-After)` between attempts and re-classifying
 * each response, so a mid-sequence 2xx returns at once and a terminal status (403, 404, 500,
 * 431, maintenance) is returned as-is. A keyless mutation is never retried (mutation guard). On
 * exhaustion the last cold response is returned, not thrown. A rejection of `baseFetch` (an
 * aborted signal, a network error) propagates unretried.
 *
 * A `Retry-After` above 120 s counts as 120 s. A caller `backoffMs` has no ceiling, so the wait
 * can pass 120 s. Each wait listens to the caller's signal by the Fetch
 * rule: `init.signal` when it is not `undefined` (`null` means none), else the signal of a
 * `Request` input. An abort ends the wait at once: the call rejects with the signal's reason and
 * sends nothing more. The default timer stops then too. An injected `sleep` runs on to its own
 * end, and its result goes nowhere (issue #3699).
 */
export function coldStartAwareFetch(baseFetch = fetch, opts = {}) {
    const maxRetries = opts.maxRetries ?? 4;
    const backoffMs = opts.backoffMs ?? coldStartBackoffMs;
    const sleep = opts.sleep;
    const wait = (ms, signal) => sleep === undefined ? abortableDelay(ms, signal) : sleepUnlessAborted(sleep, ms, signal);
    return async (input, init) => {
        let res = await baseFetch(input, init);
        let action = classifyColdStart(res);
        if (!action.retry)
            return res;
        if (!isColdStartRetryable(input, init))
            return res;
        for (let attempt = 0; action.retry; attempt += 1) {
            if (attempt >= maxRetries)
                return res;
            await wait(Math.max(backoffMs(attempt), action.retryAfterSec * 1000), callerSignalOf(input, init));
            res = await baseFetch(input, init);
            action = classifyColdStart(res);
        }
        return res;
    };
}
//# sourceMappingURL=cold-start.js.map